// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { cliMe, listTimezones, updateProfile } from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { Field, Form, ValidationMessage } from "./components/forms";
import { Heading } from "./components/layout";
import { profileFormSchema } from "./form-validation";

export function UserProfile(p: { slot: number }) {
  const queryClient = useQueryClient();
  const profile = createQuery(() => ({
    queryKey: [p.slot, "identity"],
    queryFn: () =>
      cliMe({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(p.slot),
      }),
  }));
  const timezones = createQuery(() => ({
    queryKey: [p.slot, "timezones"],
    queryFn: () =>
      listTimezones({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(p.slot),
      }),
  }));
  return (
    <>
      <Heading
        title="User profile"
        copy="Manage the personal details associated with your user profile."
      />
      <section class="panel max-w-2xl p-6">
        <Show when={profile.isPending || timezones.isPending}>
          <p>Loading profile…</p>
        </Show>
        <Show when={profile.isError || timezones.isError}>
          <div role="alert" class="alert alert-error">
            Unable to load your profile.
          </div>
        </Show>
        <Show when={profile.data?.data && timezones.data?.data.items}>
          {(_) => (
            <div class="grid gap-5">
              <div>
                <p class="font-semibold">Email</p>
                <p class="mt-1 text-slate-600">{profile.data!.data.email}</p>
              </div>
              <Form
                schema={profileFormSchema}
                submitLabel="Save profile"
                successMessage="Profile updated."
                action={async (data) => {
                  await updateProfile({
                    throwOnError: true,
                    responseStyle: "fields",
                    headers: authSlotHeaders(p.slot),
                    body: {
                      name: data.name ?? "",
                      timezone: data.timezone ?? "",
                    },
                  });
                  await queryClient.invalidateQueries({
                    queryKey: [p.slot, "identity"],
                  });
                }}
              >
                <Field
                  name="name"
                  label="Name"
                  value={profile.data!.data.name}
                  required
                  minLength={1}
                  maxLength={120}
                />
                <div class="field">
                  <label for="timezone">Timezone</label>
                  <select
                    class="select select-bordered w-full"
                    id="timezone"
                    name="timezone"
                    value={profile.data!.data.timezone}
                    required
                  >
                    <For each={timezones.data!.data.items}>
                      {(timezone) => (
                        <option value={timezone}>{timezone}</option>
                      )}
                    </For>
                  </select>
                  <p class="help">Used to display dates and times.</p>
                  <ValidationMessage name="timezone" />
                </div>
              </Form>
            </div>
          )}
        </Show>
      </section>
    </>
  );
}
